import React, { useEffect, useState, useContext, useCallback, useMemo, useRef } from "react"
import { BarChart } from "@mui/x-charts/BarChart"
import {
  HiOutlineRefresh, HiOutlineTruck, HiOutlineServer,
  HiOutlineShieldExclamation, HiOutlineClipboardList,
  HiArrowRight, HiOutlineSearch, HiOutlineX, HiOutlineFilter
} from "react-icons/hi"
import { Modal } from "components/Core"
import { post_middleware_osrm_search } from "services/middleware_services"
import AuthContext from "store/context/PublicContext"
import { format_datetime_by_local_client } from "helpers/datetime_helper"
import translatesObj from "translates/translation.json"

const statusses: any = translatesObj

/* ─── Palette ────────────────────────────────────────────────────────────── */
const COURIER_COLORS = [
  "#6366f1", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#06b6d4", "#f97316", "#ec4899",
  "#14b8a6", "#84cc16"
]

const ORDER_FIELDS = ["logisticPlan", "_omnixStateMachine", "deliveryType", "creationDate", "channel", "salesChannelId", "shippingType"]

interface OrdersByCourierWidgetProps {
  storeId?: string | null
}

const formatDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

/* ─── Column Header Filter ───────────────────────────────────────────────── */
interface ColFilterProps {
  colKey: string
  isOpen: boolean
  isActive: boolean
  onToggle: (k: string | null) => void
  onClear: () => void
  align?: "left" | "right"
  children: React.ReactNode
}

const ColFilter: React.FC<ColFilterProps> = ({ colKey, isOpen, isActive, onToggle, onClear, align = "left", children }) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (t.closest(".col-filter-trigger")) return
      if (ref.current && !ref.current.contains(t)) onToggle(null)
    }
    if (isOpen) document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [isOpen, onToggle])

  return (
    <div className="flex items-center gap-1 relative z-20">
      <button
        onClick={e => { e.stopPropagation(); onToggle(isOpen ? null : colKey) }}
        className={`col-filter-trigger p-1 rounded-md transition-all border text-[11px] ${
          isActive
            ? "bg-emerald-50 text-emerald-600 border-emerald-100"
            : isOpen
            ? "bg-indigo-50 text-indigo-600 border-indigo-100"
            : "bg-white text-gray-400 border-transparent hover:bg-gray-50 hover:border-gray-100"
        }`}
      >
        <HiOutlineFilter className="text-[11px] pointer-events-none" />
      </button>
      {isActive && (
        <button
          onClick={e => { e.stopPropagation(); onClear() }}
          className="p-1 rounded-md bg-rose-50 text-rose-400 border border-rose-100 hover:bg-rose-100 transition-colors"
        >
          <HiOutlineX className="text-[10px]" />
        </button>
      )}
      {isOpen && (
        <div
          ref={ref}
          className={`absolute top-8 z-50 shadow-2xl bg-white border border-gray-100 rounded-xl overflow-hidden animate-slide-down-fader min-w-[160px] ${align === "right" ? "right-0" : "left-0"}`}
        >
          {children}
        </div>
      )}
    </div>
  )
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
const OrdersByCourierWidget: React.FC<OrdersByCourierWidgetProps> = ({ storeId }) => {
  const [days, setDays] = useState<7 | 15>(7)
  const [labels, setLabels] = useState<string[]>([])
  const [counts, setCounts] = useState<number[]>([])
  const [colors, setColors] = useState<string[]>([])
  const [orders, setOrders] = useState<any[]>([])
  const [courierMap, setCourierMap] = useState<Record<string, string>>({})
  const [showOrders, setShowOrders] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [authError, setAuthError] = useState(false)
  const [serverError, setServerError] = useState(false)

  // Column filters
  const [openFilterCol, setOpenFilterCol] = useState<string | null>(null)
  const [searchId, setSearchId] = useState("")
  const [filterCourier, setFilterCourier] = useState("")
  const [filterDelivery, setFilterDelivery] = useState("")
  const [filterShipping, setFilterShipping] = useState("")
  const [filterChannel, setFilterChannel] = useState("")
  const [filterEstado, setFilterEstado] = useState("")

  const { user: { token } } = useContext(AuthContext)

  const fetchData = useCallback(async (periodDays: number) => {
    setIsLoading(true)
    setAuthError(false)
    setServerError(false)

    try {
      const fromDate = new Date()
      fromDate.setDate(fromDate.getDate() - periodDays)
      fromDate.setHours(0, 0, 0, 0)

      const orderFilters: Record<string, any> = {
        creationDate: { from: formatDate(fromDate), to: formatDate(new Date()) }
      }
      if (storeId) {
        orderFilters["logisticPlan.sources.data.id"] = storeId
      }

      const [couriersRes, ordersRes] = await Promise.all([
        post_middleware_osrm_search("couriers", { from: 0, size: 200, filters: {} }, token),
        post_middleware_osrm_search("orders", {
          from: 0, size: 500,
          fields: ORDER_FIELDS,
          filters: orderFilters,
          sortBy: { creationDate: "desc" }
        }, token),
      ])

      const couriers: any[] = couriersRes?.data?.message?.records || []
      const rawOrders: any[] = ordersRes?.data?.message?.records || []

      const cMap: Record<string, string> = {}
      couriers.forEach((c: any) => { if (c.id) cMap[String(c.id)] = c.name || String(c.id) })
      setCourierMap(cMap)
      setOrders(rawOrders)

      const totals: Record<string, number> = {}
      couriers.forEach((c: any) => { if (c.id) totals[c.name || String(c.id)] = 0 })

      rawOrders.forEach((order: any) => {
        const cId = order?.logisticPlan?.sources?.[0]?.route?.[0]?.courier?.id ||
                    order?.logisticPlan?.sources?.[0]?.route?.[0]?.courier?.name
        if (cId) {
          const name = cMap[String(cId)] || cId
          totals[name] = (totals[name] || 0) + 1
        } else {
          totals["Sin Courier"] = (totals["Sin Courier"] || 0) + 1
        }
      })

      const sorted = Object.entries(totals).sort((a, b) => b[1] - a[1])
      setLabels(sorted.map(([n]) => n))
      setCounts(sorted.map(([, c]) => c))
      setColors(sorted.map((_, i) => COURIER_COLORS[i % COURIER_COLORS.length]))

    } catch (err: any) {
      if (err?.response?.status === 401 || err?.response?.status === 403) setAuthError(true)
      else setServerError(true)
    } finally {
      setIsLoading(false)
    }
  }, [storeId, token])

  useEffect(() => { fetchData(days) }, [days, fetchData])

  /* ─── Unique filter options ─── */
  const courierOptions = useMemo(() => {
    const s = new Set<string>()
    orders.forEach(o => {
      const cId = o?.logisticPlan?.sources?.[0]?.route?.[0]?.courier?.id ||
                  o?.logisticPlan?.sources?.[0]?.route?.[0]?.courier?.name
      if (cId) s.add(courierMap[String(cId)] || cId)
    })
    return Array.from(s).sort()
  }, [orders, courierMap])

  const deliveryOptions = useMemo(() => {
    const s = new Set<string>()
    orders.forEach(o => { if (o?.deliveryType) s.add(o.deliveryType) })
    return Array.from(s).sort()
  }, [orders])

  const shippingOptions = useMemo(() => {
    const s = new Set<string>()
    orders.forEach(o => { if (o?.shippingType) s.add(o.shippingType) })
    return Array.from(s).sort()
  }, [orders])

  const channelOptions = useMemo(() => {
    const s = new Set<string>()
    orders.forEach(o => { if (o?.channel) s.add(o.channel) })
    return Array.from(s).sort()
  }, [orders])

  const stateOptions = useMemo(() => {
    const s = new Set<string>()
    orders.forEach(o => {
      const code = o?._omnixStateMachine?.currentState?.state?.name
      if (code) s.add(statusses[code]?.title || code)
    })
    return Array.from(s).sort()
  }, [orders])

  /* ─── Filtered orders ─── */
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const cId = order?.logisticPlan?.sources?.[0]?.route?.[0]?.courier?.id ||
                  order?.logisticPlan?.sources?.[0]?.route?.[0]?.courier?.name
      const courierDisplay = cId ? (courierMap[String(cId)] || cId) : "Sin Courier"
      const statusCode = order?._omnixStateMachine?.currentState?.state?.name || ""
      const estado = statusses[statusCode]?.title || statusCode

      if (searchId && !String(order?.salesChannelId || "").toLowerCase().includes(searchId.toLowerCase())) return false
      if (filterCourier && courierDisplay !== filterCourier) return false
      if (filterDelivery && order?.deliveryType !== filterDelivery) return false
      if (filterShipping && order?.shippingType !== filterShipping) return false
      if (filterChannel && order?.channel !== filterChannel) return false
      if (filterEstado && estado !== filterEstado) return false
      return true
    })
  }, [orders, courierMap, searchId, filterCourier, filterDelivery, filterShipping, filterChannel, filterEstado])

  const closeModal = () => { setShowOrders(false); setOpenFilterCol(null); setSearchId(""); setFilterCourier(""); setFilterDelivery(""); setFilterShipping(""); setFilterChannel(""); setFilterEstado("") }

  /* ─── Select popover helper ─── */
  const SelectFilter = ({ colKey, value, options, onChange, align = "left" }: { colKey: string; value: string; options: string[]; onChange: (v: string) => void; align?: "left" | "right" }) => (
    <ColFilter colKey={colKey} isOpen={openFilterCol === colKey} isActive={!!value} onToggle={setOpenFilterCol} onClear={() => onChange("")} align={align}>
      <div className="flex flex-col max-h-52 overflow-y-auto py-1">
        {options.map(opt => (
          <button key={opt} onClick={() => { onChange(opt); setOpenFilterCol(null) }}
            className={`px-3 py-2 text-left text-[11px] font-medium transition-colors hover:bg-gray-50 ${value === opt ? "bg-indigo-50 text-indigo-700 font-bold" : "text-gray-600"}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </ColFilter>
  )

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <>
      <div className="w-full h-full bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex flex-row items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
            <h3 className="text-[13px] font-black text-gray-800 uppercase tracking-widest">
              Órdenes por Courier
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
              {([7, 15] as const).map(d => (
                <button key={d} type="button" onClick={() => setDays(d)}
                  className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all ${days === d ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {d}d
                </button>
              ))}
            </div>
            {isLoading && <span className="h-3.5 w-3.5 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />}
            {orders.length > 0 && (
              <button type="button" onClick={() => setShowOrders(true)}
                className="flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-md text-[11px] font-bold transition-colors"
              >
                <HiOutlineClipboardList className="text-[14px]" />
                Ver listado
                <HiArrowRight className="text-[11px]" />
              </button>
            )}
            <button type="button" onClick={() => fetchData(days)} disabled={isLoading}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
            >
              <HiOutlineRefresh className={`text-[15px] ${isLoading ? "animate-spin text-indigo-400" : ""}`} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 p-4 min-h-0">
          {authError ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-center">
              <HiOutlineShieldExclamation className="text-3xl text-red-400" />
              <p className="text-[13px] font-bold text-gray-700">Sesión Expirada</p>
              <button onClick={() => { localStorage.clear(); window.location.href = "/" }}
                className="px-4 py-1.5 mt-1 bg-red-50 text-red-600 rounded-lg text-[11px] font-bold"
              >Iniciar sesión</button>
            </div>
          ) : serverError ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-center">
              <HiOutlineServer className="text-3xl text-gray-300" />
              <p className="text-[13px] font-bold text-gray-500">Error de conectividad</p>
              <button onClick={() => fetchData(days)}
                className="px-4 py-1.5 mt-1 bg-indigo-50 text-indigo-600 rounded-lg text-[11px] font-bold"
              >Reintentar</button>
            </div>
          ) : isLoading && labels.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <span className="h-6 w-6 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
              <p className="text-[12px] font-medium text-gray-400">Cargando datos...</p>
            </div>
          ) : labels.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-2">
              <HiOutlineTruck className="text-3xl text-gray-200" />
              <p className="text-[13px] font-bold text-gray-400">Sin datos en este período</p>
            </div>
          ) : (
            <BarChart
              xAxis={[{ scaleType: "band", data: labels, tickLabelStyle: { fontSize: 10, fontWeight: 700, fill: "#6b7280" } }]}
              yAxis={[{ tickLabelStyle: { fontSize: 10, fontWeight: 600, fill: "#9ca3af" } }]}
              series={[{ data: counts, label: "Órdenes", color: "#6366f1" }]}
              colors={colors}
              height={260}
              margin={{ top: 10, right: 16, bottom: 40, left: 36 }}
              slotProps={{ legend: { hidden: true } } as any}
            />
          )}
        </div>
      </div>

      {/* ── Orders List Modal ── */}
      <Modal
        mounted={showOrders}
        localIconClose={true}
        className="w-[1000px] max-w-[96vw] !p-0 !rounded-[24px] shadow-2xl bg-[#F2F2F7]/95 backdrop-blur-2xl border border-white/40 overflow-hidden tracking-tight"
      >
        <div className="w-full flex flex-col max-h-[88vh] min-h-[520px] relative">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-9 h-1 bg-gray-300/80 rounded-full z-20" />

          {/* Modal Header */}
          <div className="px-5 pt-6 pb-4 border-b border-gray-200/50 flex items-center justify-between bg-white/80 shrink-0">
            <div className="flex items-center gap-2 mt-1">
              <HiOutlineClipboardList className="text-indigo-500 text-lg" />
              <h3 className="text-[14px] font-black text-gray-900 uppercase tracking-widest">
                Listado de órdenes — {days}d
              </h3>
              <span className="ml-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[10px] font-black">
                {filteredOrders.length}{filteredOrders.length !== orders.length ? ` / ${orders.length}` : ""}
              </span>
            </div>
            <button type="button" onClick={closeModal}
              className="p-2 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-md transition-all active:scale-95 mt-1"
            >
              <HiOutlineX className="text-[15px]" />
            </button>
          </div>

          {/* Table */}
          <div className="overflow-auto flex-1 hide-scrollbar">
            <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 bg-white/90 backdrop-blur-sm z-10">
                  <tr className="border-b border-gray-100">

                    {/* N° Orden */}
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-widest whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>N° Orden</span>
                        <ColFilter colKey="orden" isOpen={openFilterCol === "orden"} isActive={!!searchId} onToggle={setOpenFilterCol} onClear={() => setSearchId("")}>
                          <div className="relative p-2">
                            <HiOutlineSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-[11px]" />
                            <input autoFocus type="text" value={searchId} onChange={e => setSearchId(e.target.value)}
                              placeholder="Buscar N° orden..."
                              className="w-full pl-6 pr-2 py-1.5 text-[11px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-300"
                            />
                          </div>
                        </ColFilter>
                      </div>
                    </th>

                    {/* Fecha */}
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-widest whitespace-nowrap">
                      Fecha creación
                    </th>

                    {/* Estado */}
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-widest whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>Estado</span>
                        <SelectFilter colKey="estado" value={filterEstado} options={stateOptions} onChange={setFilterEstado} />
                      </div>
                    </th>

                    {/* Courier */}
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-widest whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>Courier</span>
                        <SelectFilter colKey="courier" value={filterCourier} options={courierOptions} onChange={setFilterCourier} />
                      </div>
                    </th>

                    {/* Delivery */}
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-widest whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>Delivery</span>
                        <SelectFilter colKey="delivery" value={filterDelivery} options={deliveryOptions} onChange={setFilterDelivery} />
                      </div>
                    </th>

                    {/* Shipping */}
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-widest whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>Shipping</span>
                        <SelectFilter colKey="shipping" value={filterShipping} options={shippingOptions} onChange={setFilterShipping} />
                      </div>
                    </th>

                    {/* Canal */}
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-widest whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>Canal</span>
                        <SelectFilter colKey="canal" value={filterChannel} options={channelOptions} onChange={setFilterChannel} align="right" />
                      </div>
                    </th>

                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-16 text-center">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <HiOutlineSearch className="text-3xl text-gray-200" />
                          <p className="text-[13px] font-bold text-gray-400">Sin resultados para los filtros aplicados</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredOrders.map((order: any, idx: number) => {
                    const cId = order?.logisticPlan?.sources?.[0]?.route?.[0]?.courier?.id ||
                                order?.logisticPlan?.sources?.[0]?.route?.[0]?.courier?.name
                    const courierDisplay = cId ? (courierMap[String(cId)] || cId) : "—"
                    const statusCode = order?._omnixStateMachine?.currentState?.state?.name || "—"
                    const estado = statusses[statusCode]?.title || statusCode
                    const fechaFormat = order?.creationDate
                      ? format_datetime_by_local_client(order.creationDate, "dd MMM yyyy, HH:mm")
                      : "—"

                    return (
                      <tr key={idx} className="hover:bg-white/70 transition-colors">
                        <td className="px-4 py-2.5 text-[12px] font-black text-gray-900 whitespace-nowrap">
                          {order?.salesChannelId ? `#${order.salesChannelId}` : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-[11px] font-medium text-gray-500 whitespace-nowrap">
                          {fechaFormat}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                            {estado}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-[11px] font-semibold text-gray-700 whitespace-nowrap">
                          {courierDisplay}
                        </td>
                        <td className="px-4 py-2.5">
                          {order?.deliveryType
                            ? <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] font-bold uppercase">{order.deliveryType}</span>
                            : <span className="text-gray-300 text-[11px]">—</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          {order?.shippingType
                            ? <span className="px-2 py-0.5 bg-violet-50 text-violet-700 rounded text-[10px] font-bold uppercase">{order.shippingType}</span>
                            : <span className="text-gray-300 text-[11px]">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-[11px] font-medium text-gray-500 whitespace-nowrap">
                          {order?.channel || "—"}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default OrdersByCourierWidget
